/**
 * Shared asset scope picker.
 * Supports: all / location / asset_type / asset_list / asset_tag
 * Used by AlertRuleDrawer, RoundupEditor, and CommScheduleDrawer.
 */
import React from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, TextField, Chip, Stack,
} from '@mui/material';

export type ScopeType = 'all' | 'location' | 'asset_type' | 'asset_list' | 'asset_tag' | 'template' | 'asset_template';

export interface ScopeLocation { locationId: string; locationName: string; }
export interface ScopeAssetType { asset_type_id: string; type_name: string; }
export interface ScopeAsset { asset_id: string; asset_name: string; serial_number?: string; location_name?: string; }
export interface ScopeJobTag { job_tag_id: string; tag_name: string; tag_scope?: string; }
export interface ScopeTemplate { template_id: string; template_name: string; }

interface Props {
  scope: ScopeType;
  onScopeChange: (s: ScopeType) => void;

  locationId: string | null;
  onLocationChange: (id: string | null) => void;

  assetTypeIds: string[];
  onAssetTypeChange: (ids: string[]) => void;

  assetIds: string[];
  onAssetChange: (ids: string[]) => void;

  jobTagIds: string[];
  onJobTagChange: (ids: string[]) => void;

  /** Template IDs to additionally filter when scope = asset_tag */
  tagTemplateIds?: string[];
  onTagTemplateChange?: (ids: string[]) => void;

  /** Template ID for template / asset_template scopes */
  templateId?: string;
  onTemplateIdChange?: (id: string) => void;

  /** Single asset ID for asset_template scope */
  templateAssetId?: string;
  onTemplateAssetIdChange?: (id: string) => void;

  locations: ScopeLocation[];
  assetTypes: ScopeAssetType[];
  assets: ScopeAsset[];
  jobTags: ScopeJobTag[];
  /** Templates list for template scopes and asset_tag sub-filter */
  templates?: ScopeTemplate[];

  /** Extra scope option alongside type for location scope (filter by type too) */
  showTypeInLocationScope?: boolean;
  locationsEnabled?: boolean;
  locationLabel?: string;
  /** When true, show asset_tag as an option */
  showTagScope?: boolean;
  /** When true (default), show template filter under asset_tag scope */
  showTagTemplateFilter?: boolean;
  /** When true, show template + asset_template scope options (matching alerts) */
  showTemplateScope?: boolean;
  /** Custom label rendered above the cards */
  label?: string;
}

type ScopeOption = {
  value: ScopeType;
  label: string;
  desc: string;
};

const ScopePicker: React.FC<Props> = ({
  scope,
  onScopeChange,
  locationId,
  onLocationChange,
  assetTypeIds,
  onAssetTypeChange,
  assetIds,
  onAssetChange,
  jobTagIds,
  onJobTagChange,
  tagTemplateIds = [],
  onTagTemplateChange,
  templateId = '',
  onTemplateIdChange,
  templateAssetId = '',
  onTemplateAssetIdChange,
  locations,
  assetTypes,
  assets,
  jobTags,
  templates = [],
  showTypeInLocationScope = true,
  locationsEnabled = true,
  locationLabel = 'Location',
  showTagScope = true,
  showTagTemplateFilter = true,
  showTemplateScope = false,
  label,
}) => {
  const opts: ScopeOption[] = [
    { value: 'all',        label: 'All assets',         desc: 'Every active asset in the business' },
    ...(locationsEnabled ? [{ value: 'location' as ScopeType, label: `By ${locationLabel}`, desc: `All assets at a specific ${locationLabel.toLowerCase()}` }] : []),
    { value: 'asset_type', label: 'By asset type',      desc: 'All assets of the selected type(s)' },
    { value: 'asset_list', label: 'Specific assets',    desc: 'Hand-pick individual assets' },
    ...(showTagScope ? [{ value: 'asset_tag' as ScopeType, label: 'By asset tag', desc: 'Assets carrying a specific tag' }] : []),
    ...(showTemplateScope ? [
      { value: 'template' as ScopeType,       label: 'By template',          desc: 'Checks using a specific template — all assets' },
      { value: 'asset_template' as ScopeType, label: 'Asset + template',     desc: 'A specific asset using a specific template' },
    ] : []),
  ];

  return (
    <Box>
      {label && (
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}
        >
          {label}
        </Typography>
      )}

      {/* Scope option cards */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {opts.map(opt => {
          const selected = scope === opt.value;
          return (
            <Box
              key={opt.value}
              onClick={() => onScopeChange(opt.value)}
              sx={{
                flex: '1 1 140px',
                p: 1.5,
                border: '2px solid',
                borderRadius: 2,
                cursor: 'pointer',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'primary.50' : 'background.paper',
                transition: 'border-color .15s, background-color .15s',
                userSelect: 'none',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={700}
                color={selected ? 'primary.main' : 'text.primary'}
                sx={{ mb: 0.25 }}
              >
                {opt.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Sub-selectors */}
      {scope === 'location' && locationsEnabled && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl fullWidth size="small" sx={{ flex: '1 1 200px' }}>
            <InputLabel>{locationLabel}</InputLabel>
            <Select
              value={locationId ?? ''}
              label={locationLabel}
              onChange={e => onLocationChange(e.target.value || null)}
            >
              <MenuItem value=""><em>All {locationLabel.toLowerCase()}s</em></MenuItem>
              {locations.map(l => (
                <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {showTypeInLocationScope && (
            <Autocomplete
              multiple
              size="small"
              sx={{ flex: '1 1 200px' }}
              options={assetTypes}
              getOptionLabel={o => o.type_name}
              value={assetTypes.filter(t => assetTypeIds.includes(t.asset_type_id))}
              onChange={(_, v) => onAssetTypeChange(v.map(t => t.asset_type_id))}
              renderInput={p => <TextField {...p} label="Filter by type (optional)" />}
              renderTags={(v, gtp) =>
                v.map((o, i) => <Chip key={o.asset_type_id} label={o.type_name} size="small" {...gtp({ index: i })} />)
              }
            />
          )}
        </Box>
      )}

      {scope === 'asset_type' && (
        <Autocomplete
          multiple
          size="small"
          options={assetTypes}
          getOptionLabel={o => o.type_name}
          value={assetTypes.filter(t => assetTypeIds.includes(t.asset_type_id))}
          onChange={(_, v) => onAssetTypeChange(v.map(t => t.asset_type_id))}
          renderInput={p => <TextField {...p} label="Asset types *" placeholder="Select types…" />}
          renderTags={(v, gtp) =>
            v.map((o, i) => <Chip key={o.asset_type_id} label={o.type_name} size="small" {...gtp({ index: i })} />)
          }
        />
      )}

      {scope === 'asset_list' && (
        <Autocomplete
          multiple
          size="small"
          options={assets}
          getOptionLabel={o =>
            `${o.asset_name}${o.serial_number ? ` · ${o.serial_number}` : ''}${o.location_name ? ` (${o.location_name})` : ''}`
          }
          value={assets.filter(a => assetIds.includes(a.asset_id))}
          onChange={(_, v) => onAssetChange(v.map(a => a.asset_id))}
          renderInput={p => <TextField {...p} label="Assets *" placeholder="Search by name or serial…" />}
          renderTags={(v, gtp) =>
            v.map((o, i) => <Chip key={o.asset_id} label={o.asset_name} size="small" {...gtp({ index: i })} />)
          }
        />
      )}

      {scope === 'asset_tag' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Autocomplete
            multiple
            size="small"
            options={jobTags.filter(t => t.tag_scope !== 'user')}
            getOptionLabel={o => o.tag_name}
            value={jobTags.filter(t => jobTagIds.includes(t.job_tag_id))}
            onChange={(_, v) => onJobTagChange(v.map(t => t.job_tag_id))}
            renderInput={p => <TextField {...p} label="Asset tags *" placeholder="Search tags…" />}
            renderTags={(v, gtp) =>
              v.map((o, i) => <Chip key={o.job_tag_id} label={o.tag_name} size="small" {...gtp({ index: i })} />)
            }
          />

          {showTagTemplateFilter && templates.length > 0 && onTagTemplateChange && (
            <Autocomplete
              multiple
              size="small"
              options={templates}
              getOptionLabel={o => o.template_name}
              value={templates.filter(t => tagTemplateIds.includes(t.template_id))}
              onChange={(_, v) => onTagTemplateChange(v.map(t => t.template_id))}
              renderInput={p => (
                <TextField
                  {...p}
                  label="Filter by template (optional)"
                  placeholder="Any template…"
                  helperText="Only include checks that used one of these templates"
                />
              )}
              renderTags={(v, gtp) =>
                v.map((o, i) => <Chip key={o.template_id} label={o.template_name} size="small" {...gtp({ index: i })} />)
              }
            />
          )}
        </Box>
      )}

      {scope === 'template' && onTemplateIdChange && (
        <FormControl fullWidth size="small">
          <InputLabel>Check template *</InputLabel>
          <Select
            value={templateId}
            label="Check template *"
            onChange={e => onTemplateIdChange(e.target.value)}
          >
            <MenuItem value=""><em>Select a template…</em></MenuItem>
            {templates.map(t => (
              <MenuItem key={t.template_id} value={t.template_id}>{t.template_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {scope === 'asset_template' && (
        <Stack spacing={1.5}>
          {onTemplateAssetIdChange && (
            <Autocomplete
              size="small"
              options={assets}
              getOptionLabel={o =>
                `${o.asset_name}${o.serial_number ? ` · ${o.serial_number}` : ''}${o.location_name ? ` (${o.location_name})` : ''}`
              }
              value={assets.find(a => a.asset_id === templateAssetId) ?? null}
              onChange={(_, v) => onTemplateAssetIdChange(v?.asset_id ?? '')}
              renderInput={p => <TextField {...p} label="Asset *" placeholder="Search by name or serial…" />}
            />
          )}
          {onTemplateIdChange && (
            <FormControl fullWidth size="small">
              <InputLabel>Check template *</InputLabel>
              <Select
                value={templateId}
                label="Check template *"
                onChange={e => onTemplateIdChange(e.target.value)}
              >
                <MenuItem value=""><em>Select a template…</em></MenuItem>
                {templates.map(t => (
                  <MenuItem key={t.template_id} value={t.template_id}>{t.template_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default ScopePicker;
